import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import api from "@/lib/api";
import ProductPhoto from "@/components/site/ProductPhoto";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";
import { STATUS, formatPrice, productWaLink, waLink } from "@/lib/whatsapp";

const FILTERS = [
  { id: "tous", label: "Toutes les pièces" },
  { id: "maroquinerie", label: "Maroquinerie" },
  { id: "chaussures", label: "Chaussures" },
  { id: "tresors", label: "Trésors" },
];

export default function Stock() {
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("tous");

  useEffect(() => {
    document.title = "ZULIANY — Le Stock";
    api
      .get("/products")
      .then((res) => setProducts(res.data.filter((p) => p.category !== "echarpe")))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const visible = useMemo(() => {
    if (filter === "tous") return products;
    if (filter === "tresors")
      return products.filter((p) => !["maroquinerie", "chaussures"].includes(p.category));
    return products.filter((p) => p.category === filter);
  }, [products, filter]);

  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]">
      <header className="pt-10 text-center">
        <Link to="/" data-testid="stock-home-link" aria-label="Retour à l'accueil Zuliany">
          <img src="/logo-light.png" alt="Zuliany" className="mx-auto h-20 w-20 object-contain" />
        </Link>
        <p className="eyebrow mt-4">La Maison</p>
        <h1 className="mt-2 font-serif text-4xl tracking-tight sm:text-5xl">Le Stock</h1>
        <p className="mx-auto mt-3 max-w-md px-6 text-sm font-light leading-relaxed text-zinc-600">
          L'ensemble de mes pièces disponibles — maroquinerie, chaussures,
          vestiaire & plus.
        </p>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 pb-20">
        <div className="flex flex-wrap justify-center gap-2" data-testid="stock-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`stock-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                filter === f.id
                  ? "border-[#0A0A0B] bg-[#0A0A0B] text-white"
                  : "border-zinc-300 text-zinc-600 hover:border-[#0A0A0B] hover:text-[#0A0A0B]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {loaded && visible.length === 0 ? (
            <div className="mx-auto max-w-md border border-dashed border-zinc-300 px-6 py-14 text-center">
              <p className="font-serif text-2xl">Le stock se renouvelle</p>
              <p className="mt-2 text-sm font-light text-zinc-500">
                Aucune pièce dans cette sélection pour le moment — de nouvelles
                pièces arrivent très bientôt.
              </p>
              <a
                data-testid="stock-empty-whatsapp-button"
                href={waLink("Bonjour Zuliany, quelles sont les nouvelles pièces à venir ?")}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 bg-gold-gradient px-6 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-black"
              >
                <WhatsAppIcon className="h-3.5 w-3.5" />
                Me tenir informé(e)
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((p) => {
                const status = STATUS[p.status] || STATUS.available;
                return (
                  <article
                    key={p.id}
                    data-testid={`stock-card-${p.id}`}
                    className="flex h-full flex-col border border-zinc-200 bg-white"
                  >
                    <div className="relative overflow-hidden">
                      <ProductPhoto
                        photos={[p.image_url, ...(p.images || [])]}
                        alt={`${p.brand} ${p.name}`}
                        className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
                        testidPrefix="stock"
                        sold={p.status === "sold"}
                      />
                      {p.status !== "available" && (
                        <span
                          className={`absolute left-3 top-3 border bg-white/90 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${status.badge}`}
                        >
                          {status.label}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                          {p.brand}
                        </p>
                        {p.taille && (
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                            {p.taille}
                          </p>
                        )}
                      </div>
                      <h2 className="mt-1 font-serif text-xl">{p.name}</h2>
                      {p.status === "sold" && (
                        <p
                          data-testid={`soldout-stock-${p.id}`}
                          className="mt-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-red-500"
                        >
                          Sold Out
                        </p>
                      )}
                      <p className="mt-1 line-clamp-2 text-xs font-light text-zinc-500">
                        {p.condition}
                      </p>

                      {p.accessories && p.accessories.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`accessories-stock-${p.id}`}>
                          {p.accessories.map((a) => (
                            <span
                              key={a}
                              className="flex items-center gap-1 border border-zinc-200 bg-zinc-50 px-2 py-1 text-[10px] text-zinc-600"
                            >
                              <Check className="h-3 w-3 text-emerald-600" />
                              {a}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-auto flex items-center justify-between gap-3 border-t border-zinc-100 pt-4">
                        <div className="flex items-center gap-2">
                          <div>
                            {p.retail_price > p.price && (
                              <p className="text-xs text-zinc-400 line-through">
                                {formatPrice(p.retail_price)}
                              </p>
                            )}
                            <p className="font-sarabun text-lg">{formatPrice(p.price)}</p>
                          </div>
                          {p.retail_price > p.price && (
                            <span
                              data-testid={`discount-stock-${p.id}`}
                              className="border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700"
                            >
                              −{Math.round((1 - p.price / p.retail_price) * 100)} %
                            </span>
                          )}
                        </div>
                        <a
                          data-testid={`stock-whatsapp-${p.id}`}
                          href={productWaLink(p)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-gold-gradient px-4 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5"
                        >
                          <WhatsAppIcon className="h-3.5 w-3.5" />
                          Contact me
                        </a>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <footer className="pb-10 text-center">
        <Link
          data-testid="stock-admin-link"
          to="/admin"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 transition-colors hover:text-zinc-500"
        >
          Espace Pro
        </Link>
      </footer>
    </div>
  );
}
